import React, { useState } from 'react';
import PaginationControls from '../../../utils/PaginationControls';
import { UserCircleDisplay } from '../../../utils/UserCustomDisplay';
import { useTimezone } from '../../../utils/TimezoneContext';

// ── Pipeline stages — blue shades from Variables.css (darkest → lightest) ────
const PIPELINE_STAGES = [
  { key: 'DISCOVERY',    label: 'Discovery'    },
  { key: 'SCREENING',    label: 'Screening'    },
  { key: 'EXTRACTION',   label: 'Extraction'   },
  { key: 'NMA',          label: 'NMA'          },
  { key: 'RISK_OF_BIAS', label: 'Risk of Bias' },
  { key: 'REPORTS',      label: 'Reports'      },
];

// Returns 0-based index of the furthest reached stage (-1 = nothing started)
const getStageIndex = (project) => {
  // Report template created → all pills lit (Reports = 5)
  if (project.has_genbi_report) return 5;
  // Extraction model configured → Extraction (2)
  if (project.has_extraction_model) return 2;
  // Abstract screening started → Screening (1)
  if (project.p1_qc_status) return 1;
  // Articles retrieved → Discovery (0)
  if ((project.abstract_count || 0) > 0) return 0;
  // Nothing done yet → no pills lit
  return -1;
};

// ── Pipeline pill bar ─────────────────────────────────────────────────────────
const PipelineBar = ({ project, compact = false }) => {
  const activeIdx = getStageIndex(project);
  return (
    <div className="slr-pipeline-bar" title={PIPELINE_STAGES[activeIdx]?.label}>
      {PIPELINE_STAGES.map((stage, i) => (
        <div
          key={stage.key}
          className={[
            'slr-pipeline-segment',
            compact ? 'slr-compact' : '',
            i <= activeIdx ? `slr-done-${i}` : '',
          ].join(' ')}
        />
      ))}
    </div>
  );
};

// ── Active stage label ────────────────────────────────────────────────────────
const StageBadge = ({ project }) => {
  const activeIdx = getStageIndex(project);
  const stage = PIPELINE_STAGES[activeIdx];
  if (!stage) return null;
  return (
    <span className="ev-xsmall-p-dark ev-bold-med">
      {stage.label}
    </span>
  );
};

// ── Mode tag class helper ─────────────────────────────────────────────────────
const getModeTagClass = (framework) => {
  if (!framework) return 'slr-mode-tag-none';
  if (framework.includes('AI') && framework.includes('HUMAN')) return 'slr-mode-tag-human-ai';
  if (framework.includes('AI')) return 'slr-mode-tag-ai';
  return 'slr-mode-tag-human';
};

// ─────────────────────────────────────────────────────────────────────────────

const ScreeningSpaceProjectList = ({
  projects,
  total,
  page,
  setPage,
  size,
  setSize,
  search,
  setSearch,
  filterType,
  setFilterType,
  onRefresh,
  onOpenProject,
  onEditProject,
  onDeleteProject,
  onDuplicateProject,
  onOpenDataManager
}) => {

  const [searchInput, setSearchInput] = useState(search);
  const [viewMode, setViewMode] = useState('card');
  const { formatDateTime } = useTimezone();

  const handleSearchInputChange = (e) => setSearchInput(e.target.value);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const getScreeningTypeLabel = (framework) => {
    if (!framework) return 'Not Configured';
    const map = {
      'SINGLE_HUMAN_ONLY': 'Single Screening',
      'SINGLE_AI_ONLY': 'Single Screening',
      'DUAL_HUMAN_AI': 'Dual Screening',
      'DUAL_HUMAN_HUMAN': 'Dual Screening'
    };
    return map[framework] || 'Unknown';
  };

  const getReviewerTypeLabel = (framework) => {
    if (!framework) return '-';
    const map = {
      'SINGLE_HUMAN_ONLY': 'Human Only',
      'SINGLE_AI_ONLY': 'AI Only',
      'DUAL_HUMAN_AI': 'Human & AI',
      'DUAL_HUMAN_HUMAN': 'Human & Human'
    };
    return map[framework] || '-';
  };

  return (
    <div>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center mt-4">
        <p className="ev-med-p-dark-blue mb-1 ms-1">
          SLR Projects ({total || 0})
        </p>
      </div>

      <div className="ev-card-4 w-100 ev-padding-med pt-3">

        {/* ── Controls row ── */}
        <div className="row mb-0">
          <div className="col-5 position-relative">
            <input
              type="text"
              className="form-control search-input ms-0"
              placeholder="Search by name or description"
              value={searchInput}
              onChange={handleSearchInputChange}
              onKeyPress={handleSearchKeyPress}
            />
            <button className="ev-btn-lightblue-fill-round search-bar-button" onClick={handleSearch}>
              <i className="bi bi-search"></i>
            </button>
          </div>

          <div className="col-7 mt-1 d-flex align-items-center justify-content-end gap-2">
            {/* Access filter */}
            <div className="btn-group custom-btn-group-outer">
              <button
                className={`btn custom-btn-group-inner ${filterType === 'ALL' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                onClick={() => setFilterType('ALL')}
              >All</button>
              <button
                className={`btn custom-btn-group-inner ${filterType === 'PUBLIC' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                onClick={() => setFilterType('PUBLIC')}
              >Public</button>
              <button
                className={`btn custom-btn-group-inner ${filterType === 'PRIVATE' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                onClick={() => setFilterType('PRIVATE')}
              >Private</button>
            </div>

            {/* View toggle */}
            <div className="btn-group custom-btn-group-outer">
              <button
                className={`btn custom-btn-group-inner ${viewMode === 'card' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                onClick={() => setViewMode('card')}
                title="Card View"
              >
                <i className="bi bi-grid-3x3-gap"></i>
              </button>
              <button
                className={`btn custom-btn-group-inner ${viewMode === 'list' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                onClick={() => setViewMode('list')}
                title="List View"
              >
                <i className="bi bi-list-ul"></i>
              </button>
            </div>

            <button className="ev-btn-lightblue-fill-round ev-bold-reg" onClick={onRefresh}>
              <i className="bi bi-arrow-repeat"></i> Refresh
            </button>
          </div>
        </div>

        {/* ── Empty state ── */}
        {projects.length === 0 && (
          <div className="text-center py-5">
            <p className="ev-small-p-blue">No SLR projects found</p>
          </div>
        )}

        {/* ══════════════════ CARD VIEW ══════════════════ */}
        {viewMode === 'card' && projects.length > 0 && (
          <div className="row mt-1 g-3">
            {projects.map(project => (
              <div key={project.id} className="col-12 col-md-6 col-lg-4 mb-1">
                <div className="ev-card-4 h-100 p-4" role="button" onClick={() => onOpenProject(project)}>

                  {/* Top row */}
                  <div className="d-flex justify-content-between align-items-start">
                    <span className={`badge badge-sm ${project.fabric_access_level === 'PUBLIC' ? 'bg-primary' : 'bg-danger'}`}>
                      {project.fabric_access_level}
                    </span>

                    <div className="d-flex align-items-center gap-1 mb-2 pe-2">
                      {project.team_members && project.team_members.length > 0 && (
                        <div
                          className="d-inline-flex"
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                        >
                          <UserCircleDisplay users={project.team_members} maxDisplay={4} />
                        </div>
                      )}
                      {/* Edit button — commented out
                      <button
                        className="ev-btn-skyblue-fill-round ev-btn-small ev-radius-xsmall avatar-tooltip ms-1"
                        data-tooltip="Edit Project"
                        onClick={(e) => { e.stopPropagation(); onEditProject(project); }}
                      >
                        <i className="bi bi-pencil"></i>
                      </button> */}
                      <button
                        className="ev-btn-red-fill-round ev-btn-small ev-radius-xsmall avatar-tooltip"
                        data-tooltip="Delete Project"
                        onClick={(e) => { e.stopPropagation(); onDeleteProject(project); }}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>

                  {/* Meta */}
                  <div className="mt-4 mb-3">
                    <p className="ev-med-p-blue ev-bold-med mb-1 avatar-tooltip">
                      {project.name}
                    </p>
                    <p className="ev-small-p-blue mb-1">
                      <span className="ev-bold-large">Team.</span> {project.team_name}
                    </p>
                    <p className="ev-small-p-blue mb-1">
                      <span className="ev-bold-large">Created At.</span>{' '}
                      {formatDateTime(project.created_at)}
                    </p>
                    <p className="ev-small-p-blue mb-5">
                      <span className="ev-bold-large">Owner.</span>{' '}
                      {project.created_by_email}
                    </p>
                  </div>

                  {/* Pipeline bar */}
                  <div className="mt-4 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="ev-xxsmall-p-blue opacity-75">Pipeline</span>
                      <StageBadge project={project} />
                    </div>
                    <PipelineBar project={project} />
                  </div>

                  {/* Footer */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <button
                      className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg avatar-tooltip"
                      data-tooltip="Duplicate Project"
                      onClick={(e) => { e.stopPropagation(); onDuplicateProject(project); }}
                    >
                      <i className="bi bi-copy me-1"></i>
                      Copy
                    </button>
                    <div className="d-flex align-items-center gap-2">
                    <button
                      className="ev-btn-blue-fill-round ev-btn-small ev-bold-reg avatar-tooltip"
                      data-tooltip="View Data Collections linked to this SLR Project"
                      onClick={(e) => { e.stopPropagation(); onOpenDataManager(project.id); }}
                    >
                      View Data
                    </button>
                    <div className="ev-message-card2 ev-btn-xsmall d-flex align-items-center ev-radius-large px-3 py-1 pe-1">
                      <span className="me-2 ev-bold-med ev-xsmall-p-blue">
                        {getScreeningTypeLabel(project.screening_framework)}
                      </span>
                      <div className="ev-message-card5 ev-btn-xsmall ev-radius-large px-3">
                        {getReviewerTypeLabel(project.screening_framework)}
                      </div>
                    </div>
                    </div>
                  </div>

                </div>
              </div>
            ))}
          </div>
        )}

        {/* ══════════════════ LIST VIEW ══════════════════ */}
        {viewMode === 'list' && projects.length > 0 && (
          <div className="table-responsive mt-3">
            <table className="table table-borderless table-hover align-middle mb-0 slr-project-table">
              <thead>
                <tr>
                  <th className="ev-xxsmall-p-blue ev-bold-med text-center pb-2">PROJECT</th>
                  <th className="ev-xxsmall-p-blue ev-bold-med text-center pb-2">TEAM</th>
                  <th className="ev-xxsmall-p-blue ev-bold-med  text-center pb-2">MODE</th>
                  <th className="ev-xxsmall-p-blue ev-bold-med text-center pb-2">PIPELINE PROGRESS</th>
                  <th className="ev-xxsmall-p-blue ev-bold-med text-center pb-2">MEMBERS</th>
                  <th className="ev-xxsmall-p-blue ev-bold-med text-center pb-2">CREATED</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {projects.map(project => (
                  <tr key={project.id} onClick={() => onOpenProject(project)}>

                    {/* PROJECT */}
                    <td className="slr-project-name-cell ">
                      <span className={`badge badge-sm mb-1 d-inline-block ${project.fabric_access_level === 'PUBLIC' ? 'bg-primary' : 'bg-danger'}`}>
                        {project.fabric_access_level}
                      </span>
                      <p className="ev-small-p-blue ev-bold-med mb-0 text-truncate">
                        {project.name}
                      </p>
                      <span className="ev-xxsmall-p-blue d-block text-truncate opacity-50">
                        {project.created_by_email}
                      </span>
                    </td>

                    {/* TEAM */}
                    <td className="ev-small-p-blue text-nowrap text-center">
                      {project.team_name || '—'}
                    </td>

                    {/* MODE */}
                    <td className='text-center'>
                      <span className={`slr-mode-tag ev-bold-med ev-btn-xsmall ev-radius-xlarge ps-3 px-3 text-center ${getModeTagClass(project.screening_framework)}`}>
                        {getReviewerTypeLabel(project.screening_framework)}
                      </span>
                    </td>

                    {/* PIPELINE PROGRESS */}
                    <td className="slr-pipeline-td text-center">
                      <PipelineBar project={project} compact />
                      <div className="mt-1">
                        <StageBadge project={project} />
                      </div>
                    </td>

                    {/* MEMBERS */}
                    <td className='text-center'
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      {project.team_members && project.team_members.length > 0
                        ? <UserCircleDisplay users={project.team_members} maxDisplay={3} />
                        : <span className="ev-xsmall-p-blue opacity-50 text-center">—</span>
                      }
                    </td>

                    {/* CREATED */}
                    <td className="ev-xsmall-p-blue text-nowrap text-center">
                      {formatDateTime(project.created_at)}
                    </td>

                    {/* ACTIONS */}
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="d-flex align-items-center gap-1">
                        <button
                          className="ev-btn-blue-fill-round ev-btn-small ev-bold-reg"
                          onClick={() => onOpenProject(project)}
                        >
                          Open
                        </button>
                        {/* Edit button — commented out
                        <button
                          className="ev-btn-skyblue-fill-round ev-btn-small ev-radius-xsmall avatar-tooltip"
                          data-tooltip="Edit Project"
                          onClick={() => onEditProject(project)}
                        >
                          <i className="bi bi-pencil"></i>
                        </button> */}
                        <button
                          className="ev-btn-blue-outline-round ev-btn-small ev-radius-xsmall avatar-tooltip"
                          data-tooltip="Duplicate Project"
                          onClick={() => onDuplicateProject(project)}
                        >
                          <i className="bi bi-copy"></i>
                        </button>
                        <button
                          className="ev-btn-red-fill-round ev-btn-small ev-radius-xsmall avatar-tooltip"
                          data-tooltip="Delete Project"
                          onClick={() => onDeleteProject(project)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Pagination ── */}
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="ev-small-p-dark">
            Showing {projects.length} of {total} entries
          </div>
          <div className="d-flex gap-2">
            <button className="ev-btn-light-fill" disabled={page === 1} onClick={() => setPage(page - 1)}>
              <i className="bi bi-chevron-left"></i>
            </button>
            <button className="ev-btn-light-fill" disabled>
              Page {page} of {Math.ceil(total / size) || 1}
            </button>
            <button className="ev-btn-light-fill" disabled={page >= Math.ceil(total / size)} onClick={() => setPage(page + 1)}>
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
          <PaginationControls
            initialSize={size}
            onSizeChange={(newSize) => { setSize(newSize); setPage(1); }}
          />
        </div>

      </div>
    </div>
  );
};

export default ScreeningSpaceProjectList;